<template>
  <checkout-layout>
    <div class="header">
      <checkout-headline :title="$t('checkout.existingNumber.title')" :text="$t('checkout.existingNumber.text')"/>
    </div>
    <div class="ui-wrapper">
      <div>
        <h4>{{ isMobileTariff ? $t('checkout.existingNumber.enterDataHeader') : $t('checkout.existingCable.enterDataHeader') }}</h4>
        <span class="body-description">{{ descriptionText }}</span>
      </div>
      <existing-number v-if="isMobileTariff"
                       :phone="telephoneNumber"
                       :code="smsCode"
                       :otpCodeSent="otpCodeSent"
                       @telephone-number="telephoneNumber = $event"
                       @sms-code="smsCode = $event"
                       @submit-otp="submitOtpCode"
                       @verify-otp="verifyOtp"/>
      <existing-cable v-else
                      :contractNumber="contractNumber"
                      :userPass="userPass"
                      @contract-number="contractNumber = $event"
                      @user-pass="userPass = $event"
                      @submit-cable="submitCableContract"/>
      <div class="btn-wrapper">
        <BaseButton v-if="isMobileTariff" @click="verifyOtp" :disabled="!otpCodeSent || !smsCode" class="btn">
          {{ $t('checkout.existingNumber.continue') }}
        </BaseButton>
        <BaseButton v-else @click="submitCableContract" :disabled="!contractNumber || !userPass" class="btn">
          {{ $t('checkout.existingNumber.continue') }}
        </BaseButton>
      </div>
    </div>
  </checkout-layout>
</template>
<script>

import { checkoutViews } from '../config/checkoutViews'
import CheckoutHeadline from '@/components/checkout/checkout-headline'
import BaseButton from '@/components/base/base-button'
import { sendOtpCode, verifyOtpCode, verifyCable } from '@/api/checkout'
import cartComponentsProps from '../config/cartComponentsProps'
import CheckoutLayout from '../checkout-layout'
import ExistingNumber from './existing-number'
import ExistingCable from './existing-cable'

export default {
  name: 'user-identification-existing',
  props: { ...cartComponentsProps },
  components: { CheckoutLayout, BaseButton, CheckoutHeadline, ExistingNumber, ExistingCable },
  data () {
    return {
      telephoneNumber: '',
      smsCode: '',
      otpCodeSent: false,
      contractNumber: '',
      userPass: ''
    }
  },
  computed: {
    isMobileTariff () {
      return this.$props.cartData.tariffType === 'MOBILE'
    },
    descriptionText () {
      return this.isMobileTariff ? this.$t('checkout.existingNumber.enterDataDescription') : this.$t('checkout.existingCable.description')
    }
  },
  methods: {
    submitOtpCode () {
      const normalizedNumber = this.telephoneNumber.replace(/[^0-9]/g, '')
      const params = {
        cartId: this.cartData.guid,
        msisdn: normalizedNumber,
        platform: 'WEB'
      }
      sendOtpCode(params).then(res => {
        if (res.status === 201) {
          this.otpCodeSent = true
          this.toast('success', {
            message: this.$t('checkout.existingNumber.codeSendSuccess')
          })
        }
      }).catch(err => {
        this.validatePhoneNumber(err.response.data.errors[0].type)
      })
    },
    validatePhoneNumber (err) {
      if (err === 'MtmLimitOtpCodeRequestsError') {
        this.toast('error', {
          message: this.$t('checkout.existingNumber.errMessages.maxLimit')
        })
      } else if (err === 'MtmCustomerNotFoundError') {
        this.toast('error', {
          message: this.$t('checkout.existingNumber.errMessages.customerNotFound')
        })
      } else if (err === 'MtmOtpCodeExpiredError') {
        this.toast('error', {
          message: this.$t('checkout.existingNumber.errMessages.expiredOtpCode')
        })
      } else if (err) {
        this.toast('error', {
          message: this.$t('checkout.existingNumber.errMessages.error')
        })
      }
    },
    validateOtpCode (err) {
      if (err === 'ValidationError') {
        this.toast('error', {
          message: this.$t('checkout.existingNumber.errMessages.invalidFormat')
        })
      } else if (err === 'MtmInvalidOtpCodeError') {
        this.toast('error', {
          message: this.$t('checkout.existingNumber.errMessages.otpNotValid')
        })
      } else if (err === 'MtmOtpCodeExpiredError') {
        this.toast('error', {
          message: this.$t('checkout.existingNumber.errMessages.expiredOtpCode')
        })
      } else if (err === 'msisdn') {
        this.toast('error', {
          message: this.$t('checkout.existingNumber.errMessages.resendCode')
        })
      }
    },
    verifyOtp () {
      const normalizedNumber = this.telephoneNumber.replace(/[^0-9]/g, '')

      const params = {
        cartId: this.cartData.guid,
        msisdn: normalizedNumber,
        otp: this.smsCode
      }

      verifyOtpCode(params).then(res => {
        if (res.status === 201) {
          this.$emit('update-component', checkoutViews.checkoutPreview.name)
        }
      }).catch(err => {
        this.validateOtpCode(err.response.data.errors[0].type)
      })
    },
    submitCableContract () {
      this.$loader.show()
      const params = {
        cartId: this.$props.cartData.guid,
        customerId: this.userPass,
        invoiceNumber: this.contractNumber
      }
      verifyCable(params).then(response => {
        if (response.status >= 200 && response.status < 400) {
          this.$emit('update-component', checkoutViews.checkoutPreview.name)
        }
      }).catch(err => {
        this.$loader.hide()
        if (err.response.data.errors[0].type === 'MtmCustomerNotFoundError') {
          this.toast('error', {
            message: this.$t('checkout.existingCable.customerNotFound')
          })
        } else {
          this.toast('error', {
            message: this.$t('errors.serverError')
          })
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-wrapper {
  padding: 4.8rem 2.4rem;
  background: $white;
  @media (map_get($breakpoints, sm)) {
    display: flex;
    padding: 4.8rem 3.4rem;
    flex-direction: column;
    border-radius: 5px;
  }
  @media (map_get($breakpoints, md)) {
    padding: 4.8rem 7.4rem;
  }
  @media (map_get($breakpoints, lg)) {
    padding: 4.8rem 8.4rem;
  }

  h4 {
    color: $black;
    font-size: 20px;
    font-weight: 400;

    @media (map_get($breakpoints, md)) {
      font-weight: 500;
    }
  }

  .tel-number {
    font-size: 12px;
    color: $dark-grey;
    margin: 25px 0 5px 0;
  }

  .body-description {
    font-size: 14px;
  }

  @media (map_get($breakpoints, sm)) {
    .btn-wrapper {
      display: flex;
      justify-content: flex-end;
    }
  }
}

.error-msg {
  color: $primary;
  width: 280px;
  margin-top: -10px;
}

.header {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  background-color: $light-grey;
  @media (map_get($breakpoints, sm)) {
    margin-top: 150px;
    text-align: center;
  }

  h1 {
    color: $black;
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 15px;
    @media (map_get($breakpoints, sm)) {
      font-size: 32px;
      line-height: 1.31;
      font-weight: normal;
      margin-bottom: 10px;
    }
  }

  span {
    font-size: 14px;
    color: $light-black;
    @media (map_get($breakpoints, sm)) {
      font-size: 16px;
    }
  }
}
</style>
